import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ShopOutlined,
  EnvironmentOutlined,
  ArrowRightOutlined,
  ThunderboltFilled,
  InboxOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { Switch } from "antd";
import { toast } from "react-toastify";

import { getAccessToken, apiUrl } from "../../common/apis";
import {
  ORDERS_POLL_INTERVAL_MS,
  alertText,
  newOrderIds,
  orderIds,
} from "../../common/orderAlerts";
import {
  alertsReady,
  armSoundOnFirstGesture,
  enableAlerts,
  playAlertTone,
  showOrderNotification,
} from "../../common/alertChannels";
import useVisiblePoll from "../../hooks/useVisiblePoll";
import { fetchRiderState, setDutyStatus } from "../../common/dutyStatus";
import Navbar from "../../Components/Navbar/Navbar";
import styles from "./home.module.css";
import Spinner from "../../Components/Spinner";
import MapButton from "../../Components/MapButton/MapButton";

function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  // Load cached assigned orders from sessionStorage if available
  const [assignedOrders, setAssignedOrders] = useState(() => {
    try {
      const cached = sessionStorage.getItem("assignedOrders");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [unassignedOrders, setUnassignedOrders] = useState(() => {
    try {
      const cached = sessionStorage.getItem("unassignedOrders");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [assignedOrdersCount, setAssignedOrdersCount] = useState(() => {
    try {
      const cached = sessionStorage.getItem("assignedOrders");
      return cached ? JSON.parse(cached).length : 0;
    } catch {
      return 0;
    }
  });

  const [unassignedOrdersCount, setUnassignedOrdersCount] = useState(() => {
    try {
      const cached = sessionStorage.getItem("unassignedOrders");
      return cached ? JSON.parse(cached).length : 0;
    } catch {
      return 0;
    }
  });

  const [accepted, setAccepted] = useState(() => {
    return location.state?.defaultTab === "accepted";
  });

  const [loading, setLoading] = useState(() => {
    // If we have cached orders, don't show full-page blocking spinner
    const cached = sessionStorage.getItem("assignedOrders");
    return !cached;
  });

  const [alerts, setAlerts] = useState(() => alertsReady());

  const [onDuty, setOnDuty] = useState(true);
  const [dutyPending, setDutyPending] = useState(false);
  const [cashLimit, setCashLimit] = useState({ limit: null, inHand: 0, reached: false });

  const loadRiderState = useCallback(() => {
    fetchRiderState()
      .then((state) => {
        if (!state) return;
        setOnDuty(state.onDuty);
        setCashLimit({
          limit: state.codLimit,
          inHand: state.cashInHand,
          reached: state.codLimitReached,
        });
      })
      .catch(() => {
        // Keep the model default (on duty) if the flag cannot be read.
      });
  }, []);

  useEffect(() => {
    loadRiderState();
  }, [loadRiderState]);

  const handleDutyChange = async (next) => {
    setDutyPending(true);
    setOnDuty(next);
    try {
      setOnDuty(await setDutyStatus(next));
    } catch (error) {
      setOnDuty(!next);
      toast.error(next ? "Could not go online" : "Could not go off duty");
    } finally {
      setDutyPending(false);
    }
  };

  // null until the first server response, so opening the app never chimes for
  // orders that were already there.
  const seenOrderIds = useRef(null);

  useEffect(() => armSoundOnFirstGesture(), []);

  const handleEnableAlerts = async () => {
    setAlerts(await enableAlerts());
  };

  useEffect(() => {
    if (!getAccessToken()) {
      navigate("/login");
    }
  }, [navigate]);

  const getOrderTime = (time) => {
    if (!time) return "";
    const utcDateTime = new Date(time);
    const istDateTime = utcDateTime
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    return istDateTime;
  };

  const getUnassignedOrders = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;
    const config = {
      method: "GET",
      url: `${apiUrl}delivery/orders/?open_orders=true`,
      headers: { Authorization: token },
    };
    axios(config)
      .then((res) => {
        const list = res.data.results || [];
        const count = res.data.count || list.length || 0;

        if (seenOrderIds.current === null) {
          seenOrderIds.current = new Set(orderIds(list));
        } else {
          const arrived = newOrderIds(seenOrderIds.current, list);
          seenOrderIds.current = new Set(orderIds(list));
          if (arrived.length > 0) {
            playAlertTone();
            showOrderNotification(alertText(arrived.length));
          }
        }

        setUnassignedOrdersCount(count);
        setUnassignedOrders(list);
        sessionStorage.setItem("unassignedOrders", JSON.stringify(list));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const getAssignedOrders = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;
    const config = {
      method: "GET",
      url: `${apiUrl}delivery/orders/?on_going=true&page_wise=false`,
      headers: { Authorization: token },
    };

    axios(config)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setAssignedOrdersCount(list.length);
        setAssignedOrders(list);
        sessionStorage.setItem("assignedOrders", JSON.stringify(list));
      })
      .catch(() => {});
  }, []);

  // Fetch only on initial mount or when returning from app launch
  useEffect(() => {
    if (!getAccessToken()) {
      return;
    }
    getUnassignedOrders();

    // Only fetch assigned orders from server if not already cached in this session
    const cachedAssigned = sessionStorage.getItem("assignedOrders");
    if (!cachedAssigned) {
      getAssignedOrders();
    }
  }, [getUnassignedOrders, getAssignedOrders]);

  const refreshOrders = useCallback(() => {
    if (!getAccessToken()) return;
    getUnassignedOrders();
    getAssignedOrders();
  }, [getUnassignedOrders, getAssignedOrders]);

  useVisiblePoll(refreshOrders, ORDERS_POLL_INTERVAL_MS, Boolean(getAccessToken()));
  useVisiblePoll(loadRiderState, ORDERS_POLL_INTERVAL_MS, Boolean(getAccessToken()));

  const currentList = accepted ? assignedOrders : unassignedOrders;

  return (
    <div className={styles.homeContainer}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <Navbar />
        <div
          className={`${styles.onlineBadge} ${!onDuty ? styles.offDutyBadge : ""}`}
        >
          <span
            className={`${styles.onlineDot} ${!onDuty ? styles.offDutyDot : ""}`}
          />
          <span>{onDuty ? "Online & Ready" : "Off duty"}</span>
          <Switch
            size="small"
            checked={onDuty}
            loading={dutyPending}
            onChange={handleDutyChange}
            aria-label="Duty status"
          />
        </div>
        {alerts.notificationsBlocked ? (
          <span className={`${styles.alertsBtn} ${styles.alertsBlocked}`}>
            🔇 Alerts blocked in browser
          </span>
        ) : (
          !(alerts.sound && alerts.notifications) && (
            <button type="button" className={styles.alertsBtn} onClick={handleEnableAlerts}>
              🔔 Enable alerts
            </button>
          )
        )}
      </div>

      {/* Greeting Header */}
      <div className={styles.greetingSection}>
        <h2 className={styles.greetingTitle}>
          Hello, <span className={styles.greetingHighlight}>Partner 👋</span>
        </h2>
        <p className={styles.greetingSubtitle}>
          {accepted ? "Your ongoing accepted deliveries" : "New orders ready to accept today"}
        </p>
      </div>

      {/* Segmented Control Tabs */}
      {cashLimit.reached && (
        <div className={styles.cashLimitBanner} role="status">
          <strong>Cash limit reached.</strong> You hold ₹{Math.round(cashLimit.inHand)} of
          your ₹{Math.round(cashLimit.limit)} limit. Submit cash to accept new orders.
        </div>
      )}

      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tabBtn} ${!accepted ? styles.tabActive : ""}`}
          onClick={() => setAccepted(false)}
        >
          <span>New Orders</span>
          <span
            className={`${styles.badgeCount} ${
              !accepted ? styles.badgeActiveRed : ""
            }`}
          >
            {unassignedOrdersCount}
          </span>
        </button>

        <button
          className={`${styles.tabBtn} ${accepted ? styles.tabActive : ""}`}
          onClick={() => setAccepted(true)}
        >
          <span>Accepted</span>
          <span
            className={`${styles.badgeCount} ${
              accepted ? styles.badgeActiveGreen : ""
            }`}
          >
            {assignedOrdersCount}
          </span>
        </button>
      </div>

      {/* Loading Spinner */}
      {loading && <Spinner />}

      {/* Order List */}
      <div className={styles.orderList}>
        {currentList && currentList.length > 0 ? (
          currentList.map((item) => (
            <Link
              key={item.id}
              to="/order"
              state={{ order: item, unassignedOrder: !accepted }}
              className={styles.orderCard}
            >
              {/* Card Header */}
              <div className={styles.cardHeader}>
                <span className={styles.orderIdTag}>{`#FDR ${item.id}`}</span>
                <span className={styles.orderTimeTag}>
                  {getOrderTime(item.payment?.date || item.date)}
                </span>
              </div>

              {/* Restaurant Info */}
              <div className={styles.restaurantRow}>
                <div className={styles.restaurantIconWrapper}>
                  <ShopOutlined />
                </div>
                <div className={styles.restaurantDetails}>
                  <h4>{item.branch?.name || "Restaurant"}</h4>
                  <p>{item.branch?.address || "Store Location"}</p>
                </div>
                {item.food_ready_at && <span className={styles.foodReadyTag}>✓ Food ready</span>}
              </div>

              {/* Delivery Tip */}
              {Number(item.delivery_boy_tip) > 0 && (
                <div className={styles.tipBadge}>
                  <ThunderboltFilled />
                  <span>₹{item.delivery_boy_tip} Delivery Tip Included</span>
                </div>
              )}

              {/* Delivery Note */}
              {item.delivery_note && item.delivery_note.trim() !== "" && (
                <div className={styles.noteBox}>
                  <p>📝 {item.delivery_note}</p>
                </div>
              )}

              {/* Customer Address Preview */}
              {item.address && (
                <div className={styles.addressRow}>
                  <EnvironmentOutlined className={styles.addressIcon} />
                  <span>
                    {`${item.address.address1 || ""}${
                      item.address.landmark || item.address.land_mark
                        ? `, ${item.address.landmark || item.address.land_mark}`
                        : ""
                    }`}
                  </span>
                </div>
              )}

              {/* Card Footer Actions */}
              <div className={styles.cardFooter}>
                <div style={{ flex: 1, maxWidth: "160px" }}>
                  {item.address?.latitude && item.address?.longitude && (
                    <MapButton
                      latitude={item.address.latitude}
                      longitude={item.address.longitude}
                    />
                  )}
                </div>
                <div className={styles.viewDetailsLink}>
                  <span>View Details</span>
                  <ArrowRightOutlined />
                </div>
              </div>
            </Link>
          ))
        ) : (
          !loading && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>
                <InboxOutlined />
              </div>
              <h4 className={styles.emptyStateTitle}>
                {accepted ? "No Accepted Orders" : "No New Orders Available"}
              </h4>
              <p className={styles.emptyStateText}>
                {accepted
                  ? "Accept new orders from the 'New Orders' tab to start deliveries."
                  : "You're all caught up! New delivery requests will appear here."}
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default Home;
